import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cn } from "../../lib/cn";
import { IconButton } from "./IconButton";
import { Switch } from "./Switch";

export type OverflowMenuItem =
  | {
      kind?: "action";
      key: string;
      label: string;
      icon?: React.ReactNode;
      onClick: () => void;
      danger?: boolean;
      hidden?: boolean;
      disabled?: boolean;
    }
  | { kind: "separator"; key: string }
  | {
      kind: "toggle";
      key: string;
      label: string;
      checked: boolean;
      onChange: (checked: boolean) => void;
      hidden?: boolean;
    };

export interface OverflowMenuProps {
  items: OverflowMenuItem[];
  ariaLabel?: string;
  className?: string;
}

const KEBAB = (
  <svg viewBox="0 0 24 24" aria-hidden className="fill-current">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

/** claudesign ⋮ overflow menu (Radix DropdownMenu): keyboard nav + focus trap
 *  for free. Supports action / separator / inline-toggle items. */
export const OverflowMenu: React.FC<OverflowMenuProps> = ({
  items,
  ariaLabel = "More actions",
  className,
}) => {
  const visible = items.filter((i) => !("hidden" in i && i.hidden));
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <IconButton ariaLabel={ariaLabel} icon={KEBAB} className={className} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className={cn(
            "z-[1400] min-w-[180px] p-[6px] rounded-md font-sans",
            "bg-surface border border-border shadow-[0_18px_50px_rgba(0,0,0,.55)]",
          )}
        >
          {visible.map((item) => {
            if (item.kind === "separator") {
              return (
                <DropdownMenu.Separator
                  key={item.key}
                  className="my-[5px] h-px bg-border-soft"
                />
              );
            }
            if (item.kind === "toggle") {
              return (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-[10px] px-[10px] py-[8px] text-[14px] text-body"
                >
                  <span>{item.label}</span>
                  <Switch
                    checked={item.checked}
                    onChange={item.onChange}
                    ariaLabel={item.label}
                  />
                </div>
              );
            }
            return (
              <DropdownMenu.Item
                key={item.key}
                disabled={item.disabled}
                onSelect={() => item.onClick()}
                className={cn(
                  "flex items-center gap-[8px] px-[10px] py-[8px] rounded-sm text-[14px] cursor-pointer outline-none",
                  "data-[highlighted]:bg-surface-hi data-[disabled]:opacity-45 data-[disabled]:cursor-default",
                  "[&_svg]:w-[16px] [&_svg]:h-[16px]",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--cyan)] focus-visible:outline-offset-2",
                  item.danger ? "text-st-down [&_svg]:text-st-down" : "text-body",
                )}
              >
                {item.icon}
                {item.label}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default OverflowMenu;
